import type { ReferenceGuide } from './types';
export const manufacturingGuide: ReferenceGuide = {
  slug: 'manufacturing-execution-systems',
  category: 'Manufacturing',
  accent: 'manufacturing',
  title: 'Manufacturing & MES Systems',
  subtitle: 'ERP, MES, SOPs, and work order tracking — the four layers that turn a plan into a finished good, and the broken hand-offs that show up in almost every plant.',
  question: 'Where does your ERP stop and your shop floor begin?',
  description: 'A vendor-neutral guide to manufacturing systems: ERP vs MES, standard operating procedures, and work order tracking. What each layer owns across the production lifecycle.',
  readMinutes: 10,
  pdf: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hpwwowkqzxwbasxuhq2t8d/69c4a78d-9834-47a7-b2b9-f26dfaf63c78/manufacturing-execution-systems-reference-packet.pdf",
  cta: {
    headline: 'See where your work orders lose the plot',
    button: 'Map your production flow'
  },
  sections: [{
    id: 'four-pillars',
    heading: 'The four pillars of manufacturing operations',
    tocLabel: 'The four pillars',
    standfirst: 'No single system turns a plan into a finished product. Four categories of tools share that responsibility — each owning a distinct layer of production.',
    blocks: [{
      kind: 'pillars',
      columns: 4,
      items: [{
        tag: 'ERP',
        title: 'Enterprise Resource Planning',
        layer: 'The business brain',
        body: 'ERP handles planning, purchasing, inventory, and scheduling — orchestrating resources to answer what needs to be built, and when.'
      }, {
        tag: 'MES',
        title: 'Manufacturing Execution System',
        layer: 'The shop-floor brain',
        body: 'MES takes the plan from ERP and executes it in real time — dispatching work orders, guiding operators, and recording exactly what was built.',
        emphasis: 'accent'
      }, {
        tag: 'SOP',
        title: 'Standard Operating Procedures',
        layer: 'The institutional knowledge layer',
        body: 'SOPs define how each task must be performed — step sequences, safety checks, and quality criteria that enforce consistency across every shift.'
      }, {
        tag: 'WOT',
        title: 'Work Order Tracking',
        layer: 'The connective tissue',
        body: 'Ties ERP’s plan to MES execution to SOP procedure — giving every stakeholder real-time visibility into what’s in progress, complete, or at risk.',
        emphasis: 'warn'
      }]
    }]
  }, {
    id: 'lifecycle',
    heading: 'The manufacturing lifecycle at a glance',
    tocLabel: 'Lifecycle at a glance',
    standfirst: 'A production order travels through nine distinct phases before it becomes a finished good.',
    blocks: [{
      kind: 'lifecycle',
      stages: [{
        label: 'Stage 1',
        title: 'Planning & Scheduling',
        steps: [{
          n: 1,
          title: 'Demand signal',
          body: 'Sales orders, forecasts, or MRP run trigger production need.'
        }, {
          n: 2,
          title: 'Work order created',
          body: 'ERP generates work order with quantity, BOM, routing, and due date.'
        }, {
          n: 3,
          title: 'Materials confirmed',
          body: 'Inventory checked; purchasing triggered for shortfalls.'
        }],
        note: '**ERP drives this phase** — MRP calculates what, when, and how much.'
      }, {
        label: 'Stage 2',
        title: 'Shop-Floor Execution',
        steps: [{
          n: 4,
          title: 'Work order released',
          body: 'MES receives order; dispatches to workstations with linked SOPs.'
        }, {
          n: 5,
          title: 'Production executed',
          body: 'Operators follow SOPs; MES captures labor, machine, and quality data.'
        }, {
          n: 6,
          title: 'Quality gates',
          body: 'In-process inspections, hold logic, and non-conformance capture.'
        }],
        note: '**MES + SOPs drive this phase** — real-time tracking against the plan.'
      }, {
        label: 'Stage 3',
        title: 'Completion & Close',
        steps: [{
          n: 7,
          title: 'Work order closed',
          body: 'MES reports actuals back to ERP — qty, scrap, labor, downtime.'
        }, {
          n: 8,
          title: 'Inventory updated',
          body: 'ERP posts finished goods; inventory and costing records updated.'
        }, {
          n: 9,
          title: 'As-built record',
          body: 'Full traceability record stored — serialised, lot-level, or batch.'
        }],
        note: '**Work Order Tracking closes the loop** — actuals feed back into planning.',
        noteEmphasis: 'success'
      }]
    }]
  }, {
    id: 'erp-vs-mes',
    heading: 'ERP vs. MES',
    tocLabel: 'ERP vs. MES',
    standfirst: 'Planning intelligence vs. shop-floor execution.',
    blocks: [{
      kind: 'compare',
      cards: [{
        tag: 'ERP',
        title: 'Enterprise Resource Planning',
        body: 'The "plan-to-make" system. Driven by demand signals and business logic, ERP orchestrates resources, materials, and schedules. It answers: what do we need to build, and when?',
        bullets: ['Manages inventory, procurement, and MRP', 'Generates work orders from demand and BOMs', 'Owns costing, scheduling, and supplier management', 'Primary systems: SAP, Oracle, NetSuite, Epicor, Infor']
      }, {
        tag: 'MES',
        title: 'Manufacturing Execution System',
        body: 'The "make-to-record" system. MES receives work orders from ERP and executes them on the shop floor in real time. It answers: how do we actually build it, and what happened?',
        bullets: ['Dispatches work orders to workstations and operators', 'Provides digital work instructions and SOP delivery', 'Tracks labor, machine, and material consumption in real time', 'Captures quality data, non-conformances, and holds', 'Generates as-built / serialised traceability records'],
        emphasis: 'accent'
      }],
      columns: ['Attribute', 'ERP', 'MES'],
      rows: [['Perspective', 'Plan-to-make', 'Make-to-record'], ['Time horizon', 'Days to months (planning)', 'Minutes to hours (real-time execution)'], ['Primary owner', 'Operations, Finance, Supply Chain', 'Manufacturing Engineering, Plant Ops'], ['Data inputs', 'Sales orders, forecasts, BOMs, inventory', 'Work orders from ERP, SOPs, machine signals'], ['Data outputs', 'Work orders, purchase orders, schedules, cost reports', 'As-built records, labor actuals, quality data, scrap'], ['Key consumers', 'Planners, buyers, finance, supply chain', 'Operators, supervisors, quality engineers'], ['Lives where', 'Business layer — integrated with finance and procurement', 'Shop-floor layer — integrated with machines and terminals'], ['Change cadence', 'Driven by demand shifts, supplier changes, BOM updates', 'Driven by routing changes, SOP revisions, line events'], ['Core risk if absent', 'No coordinated plan; stockouts, overbuilds, missed delivery', 'No execution visibility; inconsistent quality, no traceability']]
    }, {
      kind: 'callout',
      variant: 'warning',
      title: 'The core gap',
      body: 'ERP ends where the shop floor begins. It creates the work order but cannot see what happens during production. MES fills that gap — but only if the two systems are integrated. When they aren’t, actuals never feed back to ERP and the plan drifts from reality.'
    }]
  }, {
    id: 'sops-and-tracking',
    heading: 'SOPs & work order tracking',
    tocLabel: 'SOPs & tracking',
    standfirst: 'The procedure and visibility layers that connect plan to execution.',
    blocks: [{
      kind: 'compare',
      cards: [{
        tag: 'SOP',
        title: 'Standard Operating Procedures',
        body: 'SOPs are the institutional knowledge of the factory floor. They define exactly how each task must be performed — step sequence, tooling, torque values, safety checks, quality criteria, and approved materials. Without them, every operator makes independent decisions.',
        bullets: ['Define approved methods for every production step', 'Enforce quality standards and safety requirements', 'Enable new operator onboarding and cross-training', 'Support regulatory compliance (ISO, FDA, AS9100)', 'Must be versioned, approved, and linked to specific work orders']
      }, {
        tag: 'WOT',
        title: 'Work Order Tracking',
        body: 'The real-time visibility layer that ties every other system together. It answers the question every supervisor, planner, and plant manager asks constantly: where is this order, and is it on track?',
        bullets: ['Tracks status from release to in-process to complete', 'Captures labor hours, machine time, and material consumption', 'Surfaces bottlenecks, delays, and non-conformances in real time', 'Provides the data for OEE, cycle time, and yield analysis', 'Creates the audit trail for regulatory and customer traceability']
      }],
      columns: [],
      rows: []
    }, {
      kind: 'prose',
      paragraphs: ['Most manufacturers have SOPs. Far fewer have SOPs that are consistently followed. The gap is a delivery problem, not a content problem — when procedures live in binders or shared drives, operators default to memory. Digital SOP delivery, surfaced at the workstation at the moment of need, closes this gap and can require sign-off before the next step proceeds.']
    }, {
      kind: 'callout',
      variant: 'flag',
      title: 'Red flag',
      body: 'If your SOPs are in a shared folder and operators are expected to look them up on their own, your procedures are not being followed consistently — regardless of how well written they are. Procedure delivery is as important as procedure content.'
    }, {
      kind: 'prose',
      paragraphs: ['Effective work order tracking has three properties: real-time status — every order’s position is visible without walking the floor; actuals capture — labor, scrap, and downtime recorded as they happen, not reconstructed at shift end; and exception alerting — the right people notified the moment an order falls behind or hits a hold.']
    }, {
      kind: 'callout',
      variant: 'tip',
      title: 'Bottom line',
      body: 'SOPs define how to build correctly. Work order tracking confirms that it happened. Together they create the evidence layer that separates manufacturers who believe they’re building to standard from those who can prove it.'
    }]
  }, {
    id: 'full-lifecycle',
    heading: 'The full manufacturing lifecycle',
    tocLabel: 'The full lifecycle',
    standfirst: 'Each phase has a primary system in the driver’s seat, but all four layers are active throughout.',
    blocks: [{
      kind: 'lifecycle',
      stages: [{
        label: 'Phase 1',
        title: 'Demand & Planning (ERP)',
        steps: [{
          n: 1,
          title: 'Demand captured',
          body: 'Sales order, forecast, or min/max triggers MRP run.'
        }, {
          n: 2,
          title: 'BOM & routing pulled',
          body: 'ERP resolves MBOM and routing for the product to be built.'
        }, {
          n: 3,
          title: 'Work order issued',
          body: 'ERP creates work order with qty, dates, materials, and routing.'
        }],
        note: '**ERP owns this phase.** SOPs are linked to operations at this stage but not yet delivered.'
      }, {
        label: 'Phase 2',
        title: 'Shop-Floor Execution (MES + SOPs)',
        steps: [{
          n: 4,
          title: 'Order released to MES',
          body: 'MES receives the work order and dispatches to the first workstation.'
        }, {
          n: 5,
          title: 'SOPs delivered',
          body: 'MES surfaces the correct, versioned SOP at the point of execution.'
        }, {
          n: 6,
          title: 'Build performed',
          body: 'Operator executes per SOP; MES captures labor, materials, machine data.'
        }],
        note: '**MES drives execution.** SOPs enforce consistency. Work Order Tracking records every step.'
      }, {
        label: 'Phase 3',
        title: 'Quality & Inspection (MES + SOPs)',
        steps: [{
          n: 7,
          title: 'In-process checks',
          body: 'Quality gates triggered per SOP; pass/fail recorded in MES.'
        }, {
          n: 8,
          title: 'Non-conformance',
          body: 'Defects flagged; NCR raised; hold applied; disposition routed for review.'
        }, {
          n: 9,
          title: 'Final inspection',
          body: 'Completed unit inspected against spec; sign-off captured in MES.'
        }],
        note: '**Quality is embedded in the SOP** and enforced by MES — not performed as an afterthought.'
      }, {
        label: 'Phase 4',
        title: 'Close & Record (Work Order Tracking to ERP)',
        steps: [{
          n: 10,
          title: 'Work order closed',
          body: 'MES marks complete; actuals (labor, scrap, yield) sent back to ERP.'
        }, {
          n: 11,
          title: 'Inventory posted',
          body: 'ERP updates finished goods inventory; components consumed.'
        }, {
          n: 12,
          title: 'Traceability record',
          body: 'Full as-built record stored — serial, lot, operator, machine, revision.'
        }],
        note: '**Work Order Tracking closes the loop.** Actuals flow back to ERP to sharpen the next plan.',
        noteEmphasis: 'success'
      }]
    }, {
      kind: 'prose',
      paragraphs: ['In practice, most manufacturers have at least one broken hand-off in this chain. ERP and MES aren’t integrated, so work orders are re-keyed by hand. SOPs aren’t linked to operations, so the right procedure doesn’t reach the operator. Work order actuals aren’t captured in real time, so ERP’s inventory and cost data is always stale.', 'Each gap is a reconciliation risk — and they compound. A factory where all four layers are siloed is a factory running on heroics, not systems.']
    }]
  }, {
    id: 'capability-matrix',
    heading: 'Capability ownership matrix',
    tocLabel: 'Capability matrix',
    standfirst: 'Which system owns which part of the manufacturing execution lifecycle.',
    blocks: [{
      kind: 'matrix',
      columns: ['ERP', 'MES', 'SOPs', 'Work Order Tracking'],
      rows: [{
        capability: 'Demand planning & MRP',
        values: [2, 0, 0, 0]
      }, {
        capability: 'Work order creation & scheduling',
        values: [2, 1, 0, 1]
      }, {
        capability: 'Inventory & materials management',
        values: [2, 1, 0, 0]
      }, {
        capability: 'Procurement & supplier management',
        values: [2, 0, 0, 0]
      }, {
        capability: 'Cost rollup & standard costing',
        values: [2, 0, 0, 1]
      }, {
        capability: 'Work order dispatch to shop floor',
        values: [1, 2, 0, 1]
      }, {
        capability: 'Digital work instruction delivery',
        values: [0, 2, 2, 0]
      }, {
        capability: 'SOP authoring, versioning & approval',
        values: [0, 1, 2, 0]
      }, {
        capability: 'Operator sign-off & step confirmation',
        values: [0, 2, 1, 1]
      }, {
        capability: 'Real-time production tracking',
        values: [0, 2, 0, 2]
      }, {
        capability: 'Labor & machine time capture',
        values: [0, 2, 0, 2]
      }, {
        capability: 'In-process quality & inspection',
        values: [0, 2, 2, 1]
      }, {
        capability: 'Non-conformance & hold management',
        values: [0, 2, 1, 1]
      }, {
        capability: 'As-built & serialised traceability',
        values: [0, 2, 0, 2]
      }, {
        capability: 'Regulatory & compliance documentation',
        values: [1, 1, 2, 2]
      }, {
        capability: 'OEE, yield & performance analytics',
        values: [1, 1, 0, 2]
      }, {
        capability: 'Actuals feedback to planning',
        values: [2, 1, 0, 2]
      }]
    }, {
      kind: 'callout',
      variant: 'tip',
      title: 'How to read this',
      body: 'No single system owns the full manufacturing execution lifecycle. ERP plans and coordinates resources; MES executes and records; SOPs define approved methods; Work Order Tracking provides visibility and closes the feedback loop. Successful manufacturing operations are really about the integration between these four layers — not any one system in isolation.'
    }]
  }, {
    id: 'where-thruline-fits',
    heading: 'Where Thruline fits in',
    tocLabel: 'Where Thruline fits',
    blocks: [{
      kind: 'thrulineFit',
      intro: 'Thruline was built for the integration gap that exists in nearly every mid-market manufacturer — the space between what ERP plans and what actually happens on the shop floor. Depending on your current system landscape, Thruline plugs in one of two ways.',
      scenarios: [{
        n: 1,
        condition: 'If you already have an ERP and/or MES in place',
        headline: 'Thruline is the integration and execution layer that keeps ERP and MES in sync',
        body: 'Thruline sits between your ERP and MES, holding SOPs and work orders as the single source of truth. Work orders flow from ERP into Thruline, enriched with versioned SOPs before dispatch to the floor. When execution completes, actuals flow back to both systems automatically — no re-keying, no drift.',
        flow: [{
          label: 'Planning',
          title: 'ERP',
          body: 'Demand, scheduling, MRP'
        }, {
          label: 'Integration & execution',
          title: 'SOPs + Work Orders',
          body: 'Single source of truth; keeps ERP & MES in sync',
          owned: true
        }, {
          label: 'Execution',
          title: 'MES',
          body: 'Shop-floor data, machine signals'
        }],
        flowConnectors: ['sync', 'sync'],
        footnote: 'The connection is bidirectional: when Thruline captures a hold, routing change, or schedule slip, it syncs that state back to both ERP and MES automatically — keeping the plan and reality aligned in real time.'
      }, {
        n: 2,
        condition: 'If you don’t yet have an MES — or your SOPs are still on paper',
        headline: 'Thruline can own the full execution layer in one place',
        body: 'For manufacturers running MES-less — off printed travelers, binder SOPs, and whiteboard tracking — Thruline provides the full execution stack without a traditional MES implementation.',
        features: [{
          title: 'Digital work orders',
          body: 'Create or import work orders; dispatch to workstations instantly — no paper travelers.'
        }, {
          title: 'SOP authoring & delivery',
          body: 'Build, version, approve, and surface SOPs at the exact step where they’re needed.'
        }, {
          title: 'Real-time shop floor tracking',
          body: 'Live status on every active order — who’s working it, where it is, and what’s at risk.'
        }, {
          title: 'Quality & traceability',
          body: 'In-process checks, NCR capture, hold management, and full as-built records in one system.'
        }]
      }],
      solutions: [{
        label: 'Explore Manufacturing MES',
        to: '/solutions/manufacturing-mes'
      }, {
        label: 'Explore Quality',
        to: '/solutions/quality'
      }]
    }, {
      kind: 'callout',
      variant: 'bottomLine',
      title: 'The bottom line',
      body: 'Whether you already have an ERP and MES or you’re still running on clipboards and spreadsheets, Thruline meets you where you are. It either bridges your existing systems — connecting ERP to the floor and closing the actuals loop — or it replaces the need for a standalone MES altogether.'
    }]
  }]
};